import {
  Airline,
  Airport,
  BookedFlightItineraryWithDepartureTime,
  CarReservation,
  CarItineraryResponseSuccess,
  CallState,
  MyTripsFilter,
  WatchAlertView,
  HotelItinerary,
  IOpenModal,
  CancellationFareRulesSuccess,
  CancellationResponse,
  CarItineraryResponseEnum,
  IRequestPassengerEdit,
  AvailabilityResponse,
  IResult,
  GuestsSelection,
  TripCategory,
  HotelCfarCancelScenario,
  HotelCfarCancelScenarioEnum,
  Payment,
  HomesItinerary,
  ActiveCrossSellOffer,
  PackageItinerary,
  AncillaryPostBookingMyTripsOffer,
  TripSummaryContext,
  ExperienceReservation,
  ExperienceAvailability,
} from "redmond";
import { actions, actionTypes } from "../actions";
import {
  ActiveExperiments,
  HotelCfarRefundDisplayVariantType,
  DefaultVariantType,
} from "../../../context/experiments";

/**
 * Returns partial call state updates based on which itinerary states were requested.
 * All itinerary state enums share the same string values ("Canceled", "Past", "Present", "Future").
 */
const getCallStateUpdate = (
  requestedStates: string[],
  callState: CallState,
  currentCallState: { [key: string]: CallState },
): { [key: string]: CallState } | null => {
  if (
    requestedStates.includes("Canceled") &&
    requestedStates.includes("Past")
  ) {
    return { ...currentCallState, past: callState, canceled: callState };
  }
  if (
    requestedStates.includes("Present") &&
    requestedStates.includes("Future")
  ) {
    return { ...currentCallState, present: callState, future: callState };
  }
  if (requestedStates.includes("Canceled")) {
    return { ...currentCallState, canceled: callState };
  }
  if (requestedStates.includes("Past")) {
    return { ...currentCallState, past: callState };
  }
  return null;
};

export enum HotelAvailabilityCallState {
  NotCalled = "NotCalled",
  InitialSearchCallInProcess = "InitialSearchCallInProcess",
  FollowUpSearchCallInProcess = "FollowUpSearchCallInProcess",
  InitialSearchCallSuccess = "InitialSearchCallSuccess",
  FollowUpSearchCallSuccess = "FollowUpSearchCallSuccess",
  // when a FollowUpSearch returns empty lodgings
  Complete = "Complete",
  Failed = "Failed",
}
export interface ITripsListState
  extends IWatchState,
  ITripsListExperimentState {
  tripsFilter: MyTripsFilter;
  itineraries: { [key: string]: BookedFlightItineraryWithDepartureTime[] };
  homeItineraries: { [key: string]: HomesItinerary[] };
  hotelItineraries: { [key: string]: HotelItinerary[] };
  carItineraries: { [key: string]: CarReservation[] };
  packageItineraries: { [key: string]: PackageItinerary[] };
  experienceItineraries: { [key: string]: ExperienceReservation[] };
  airports: { [key: string]: Airport };
  airlines: { [key: string]: Airline };
  myTripsPostBookingOffers: AncillaryPostBookingMyTripsOffer[];
  selectedFlight: BookedFlightItineraryWithDepartureTime | null;
  selectedHotel: HotelItinerary | null;
  selectedHome: HomesItinerary | null;
  selectedCar: CarReservation | null;
  selectedPackage: PackageItinerary | null;
  selectedPackageHotel: HotelItinerary | null;
  selectedPackageFlight: BookedFlightItineraryWithDepartureTime | null;
  selectedExperience: ExperienceReservation | null;
  fetchFlightsCallState: { [key: string]: CallState };
  fetchHotelsCallState: { [key: string]: CallState };
  fetchHomesCallState: { [key: string]: CallState };
  fetchCarsCallState: { [key: string]: CallState };
  fetchPackagesCallState: { [key: string]: CallState };
  fetchExperiencesCallState: { [key: string]: CallState };
  acceptScheduleChangeCallState: CallState;
  denyScheduleChangeCallState: CallState;
  openModal: IOpenModal;
  requestPassengerEdit: IRequestPassengerEdit | null;
  selectedCfar: CancellationFareRulesSuccess | null;
  selectedCfarItineraryId: string | null;
  fetchFlightCfarInfoCallState: CallState;
  fetchFlightCfarInfoCallExpires: number | null;
  flightCfarCancellationResponse: CancellationResponse | null;
  confirmFlightCfarCancellationCallState: CallState;
  hotelAvailabilityResponse?: AvailabilityResponse;
  hotelSearchFromDate: Date | null;
  hotelSearchUntilDate: Date | null;
  hotelSearchLocationResult: IResult | null;
  hotelSearchAdultsCount: number;
  hotelSearchChildrenCount: number[];
  hotelSelectedLodgingIndex: number | null;
  hotelAvailabilityCallState: HotelAvailabilityCallState;
  flightToHotelCrossSellTripCategory: TripCategory;
  hotelCfarCancellationPolicyScenario: HotelCfarCancelScenario | null;
  hotelCfarCancellationPolicyCallState: CallState;
  hotelCfarCancellationPolicyCallExpires: number | null;
  selectedHotelCfarReservationId: string | null;
  confirmHotelCfarCancellationCallState: CallState;
  crossSellLatency: number | null;
  paymentMethods: Payment[];
  listPaymentMethodCallState: CallState;
  searchQuery: string;
  crossSellOffers: ActiveCrossSellOffer[];
  fetchHotelCrossSellCallState: CallState;
  activitiesXsellExperiences: ExperienceAvailability[];
  activitiesXsellCallState: CallState;
}

export interface ITripsListExperimentState {
  experiments: ITripsListExperiments | null;
}

export interface ITripsListExperiments {
  [ActiveExperiments.HOTEL_CFAR_REFUND_DISPLAY]: HotelCfarRefundDisplayVariantType;
  [ActiveExperiments.FINTECH_CSAT]: DefaultVariantType;
  [ActiveExperiments.CORP_MYTRIPS_REPLATFORMING]: DefaultVariantType;
  [ActiveExperiments.TRIPS_REPLATFORM_BANNER_NOTICE]: DefaultVariantType;
}

export const getExperimentState = (
  state: ITripsListState | undefined
): ITripsListExperimentState => ({
  experiments: state?.experiments ?? initialState.experiments,
});

export interface IWatchState {
  watches: WatchAlertView[];
  watchContext: TripSummaryContext | null;
  updateWatchCallState: CallState;
  listWatchCallState: CallState;
  deleteWatchCallState: CallState;
}

const initialState: ITripsListState = {
  experiments: null,
  tripsFilter: MyTripsFilter.UPCOMING_TRIPS,
  watchContext: null,
  selectedFlight: null,
  selectedHotel: null,
  selectedHome: null,
  selectedCar: null,
  selectedPackage: null,
  selectedPackageFlight: null,
  selectedPackageHotel: null,
  selectedExperience: null,
  itineraries: {
    canceled: [],
    past: [],
    present: [],
    future: [],
    requests: [],
  },
  hotelItineraries: {
    canceled: [],
    past: [],
    present: [],
    future: [],
    requests: [],
  },
  homeItineraries: {
    canceled: [],
    past: [],
    present: [],
    future: [],
    requests: [],
  },
  carItineraries: {
    canceled: [],
    past: [],
    present: [],
    future: [],
    requests: [],
  },
  packageItineraries: {
    canceled: [],
    past: [],
    present: [],
    future: [],
    requests: [],
  },
  experienceItineraries: {
    canceled: [],
    past: [],
    present: [],
    future: [],
    requests: [],
  },
  airports: {},
  airlines: {},
  watches: [],
  deleteWatchCallState: CallState.NotCalled,
  updateWatchCallState: CallState.NotCalled,
  listWatchCallState: CallState.NotCalled,
  fetchFlightsCallState: {
    canceled: CallState.NotCalled,
    past: CallState.NotCalled,
    present: CallState.NotCalled,
    future: CallState.NotCalled,
    requests: CallState.NotCalled,
  },
  fetchHotelsCallState: {
    canceled: CallState.NotCalled,
    past: CallState.NotCalled,
    present: CallState.NotCalled,
    future: CallState.NotCalled,
    requests: CallState.NotCalled,
  },
  fetchHomesCallState: {
    canceled: CallState.NotCalled,
    past: CallState.NotCalled,
    present: CallState.NotCalled,
    future: CallState.NotCalled,
    requests: CallState.NotCalled,
  },
  fetchCarsCallState: {
    canceled: CallState.NotCalled,
    past: CallState.NotCalled,
    present: CallState.NotCalled,
    future: CallState.NotCalled,
    requests: CallState.NotCalled,
  },
  fetchPackagesCallState: {
    canceled: CallState.NotCalled,
    past: CallState.NotCalled,
    present: CallState.NotCalled,
    future: CallState.NotCalled,
    requests: CallState.NotCalled,
  },
  fetchExperiencesCallState: {
    canceled: CallState.NotCalled,
    past: CallState.NotCalled,
    present: CallState.NotCalled,
    future: CallState.NotCalled,
    requests: CallState.NotCalled,
  },
  acceptScheduleChangeCallState: CallState.NotCalled,
  denyScheduleChangeCallState: CallState.NotCalled,
  openModal: { type: null, selectedItinerary: null },
  requestPassengerEdit: null,
  selectedCfar: null,
  selectedCfarItineraryId: null,
  fetchFlightCfarInfoCallState: CallState.NotCalled,
  fetchFlightCfarInfoCallExpires: null,
  flightCfarCancellationResponse: null,
  confirmFlightCfarCancellationCallState: CallState.NotCalled,
  hotelSearchFromDate: null,
  hotelSearchUntilDate: null,
  hotelSearchLocationResult: null,
  hotelSearchAdultsCount: 2,
  hotelSearchChildrenCount: [],
  hotelSelectedLodgingIndex: null,
  hotelAvailabilityCallState: HotelAvailabilityCallState.NotCalled,
  flightToHotelCrossSellTripCategory: TripCategory.ROUND_TRIP,
  hotelCfarCancellationPolicyScenario: null,
  hotelCfarCancellationPolicyCallState: CallState.NotCalled,
  hotelCfarCancellationPolicyCallExpires: null,
  selectedHotelCfarReservationId: null,
  confirmHotelCfarCancellationCallState: CallState.NotCalled,
  crossSellLatency: null,
  paymentMethods: [],
  listPaymentMethodCallState: CallState.NotCalled,
  searchQuery: "",
  crossSellOffers: [],
  fetchHotelCrossSellCallState: CallState.NotCalled,
  myTripsPostBookingOffers: [],
  activitiesXsellExperiences: [],
  activitiesXsellCallState: CallState.NotCalled,
};

export function reducer(
  state: ITripsListState = initialState,
  action: actions.TripsListActions
): ITripsListState {
  switch (action.type) {
    case actionTypes.SET_TRIPS_LIST_EXPERIMENTS:
      return { ...state, experiments: action.experiments };

    case actionTypes.SET_MY_TRIPS_FILTER:
      const { tripsFilter } = action;
      return { ...state, tripsFilter };

    case actionTypes.FETCH_FLIGHTS: {
      const updated = getCallStateUpdate(action.request.states, CallState.InProcess, state.fetchFlightsCallState);
      return updated ? { ...state, fetchFlightsCallState: updated } : { ...state };
    }
    case actionTypes.FETCH_FLIGHTS_FAILED: {
      const updated = getCallStateUpdate(action.request.states, CallState.Failed, state.fetchFlightsCallState);
      return updated ? { ...state, fetchFlightsCallState: updated } : { ...state };
    }
    case actionTypes.SET_FLIGHTS: {
      const updated = getCallStateUpdate(action.itineraryTypes, CallState.Success, state.fetchFlightsCallState);
      return updated
        ? { ...state, fetchFlightsCallState: updated, itineraries: action.flights, airports: action.airports, airlines: action.airlines }
        : { ...state };
    }
    case actionTypes.SET_MY_TRIPS_POSTBOOKING_OFFERS:
      return {
        ...state,
        myTripsPostBookingOffers: action.response,
      };
    case actionTypes.FETCH_HOTELS: {
      const updated = getCallStateUpdate(action.request.states, CallState.InProcess, state.fetchHotelsCallState);
      return updated ? { ...state, fetchHotelsCallState: updated } : { ...state };
    }
    case actionTypes.FETCH_HOTELS_FAILED: {
      const updated = getCallStateUpdate(action.request.states, CallState.Failed, state.fetchHotelsCallState);
      return updated ? { ...state, fetchHotelsCallState: updated } : { ...state };
    }
    case actionTypes.SET_HOTELS: {
      const updated = getCallStateUpdate(action.itineraryTypes, CallState.Success, state.fetchHotelsCallState);
      return updated
        ? { ...state, fetchHotelsCallState: updated, hotelItineraries: action.hotels }
        : { ...state };
    }
    case actionTypes.FETCH_HOMES: {
      const updated = getCallStateUpdate(action.request.states, CallState.InProcess, state.fetchHomesCallState);
      return updated ? { ...state, fetchHomesCallState: updated } : { ...state };
    }
    case actionTypes.FETCH_HOMES_FAILED: {
      const updated = getCallStateUpdate(action.request.states, CallState.Failed, state.fetchHomesCallState);
      return updated ? { ...state, fetchHomesCallState: updated } : { ...state };
    }
    case actionTypes.SET_HOMES: {
      const updated = getCallStateUpdate(action.itineraryTypes, CallState.Success, state.fetchHomesCallState);
      return updated
        ? { ...state, fetchHomesCallState: updated, homeItineraries: action.homes }
        : { ...state };
    }
    case actionTypes.FETCH_CARS: {
      const updated = getCallStateUpdate(action.request.states, CallState.InProcess, state.fetchCarsCallState);
      return updated ? { ...state, fetchCarsCallState: updated } : { ...state };
    }

    case actionTypes.FETCH_CARS_FAILED: {
      const updated = getCallStateUpdate(action.request.states, CallState.Failed, state.fetchCarsCallState);
      return updated ? { ...state, fetchCarsCallState: updated } : { ...state };
    }

    case actionTypes.SET_CARS: {
      const { response: carResponse } = action;
      const types = action.reservationTypes || [];
      if (
        carResponse.CarItineraryResponse === CarItineraryResponseEnum.Failure
      ) {
        const updated = getCallStateUpdate(types, CallState.Failed, state.fetchCarsCallState);
        return updated ? { ...state, fetchCarsCallState: updated } : { ...state };
      }
      const updated = getCallStateUpdate(types, CallState.Success, state.fetchCarsCallState);
      return updated
        ? { ...state, fetchCarsCallState: updated, carItineraries: (carResponse as CarItineraryResponseSuccess).reservations }
        : { ...state };

    }

    case actionTypes.FETCH_PACKAGES: {
      const updated = getCallStateUpdate(action.request.states, CallState.InProcess, state.fetchPackagesCallState);
      return updated ? { ...state, fetchPackagesCallState: updated } : { ...state };
    }
    case actionTypes.FETCH_PACKAGES_FAILED: {
      const updated = getCallStateUpdate(action.request.states, CallState.Failed, state.fetchPackagesCallState);
      return updated ? { ...state, fetchPackagesCallState: updated } : { ...state };
    }
    case actionTypes.SET_PACKAGES: {
      const updated = getCallStateUpdate(action.itineraryTypes, CallState.Success, state.fetchPackagesCallState);
      return updated
        ? { ...state, fetchPackagesCallState: updated, packageItineraries: action.packages }
        : { ...state };
    }

    case actionTypes.FETCH_EXPERIENCES: {
      const updated = getCallStateUpdate(action.request.states, CallState.InProcess, state.fetchExperiencesCallState);
      return updated ? { ...state, fetchExperiencesCallState: updated } : { ...state };
    }
    case actionTypes.FETCH_EXPERIENCES_FAILED: {
      const updated = getCallStateUpdate(action.request.states, CallState.Failed, state.fetchExperiencesCallState);
      return updated ? { ...state, fetchExperiencesCallState: updated } : { ...state };
    }
    case actionTypes.SET_EXPERIENCES: {
      const updated = getCallStateUpdate(action.itineraryTypes, CallState.Success, state.fetchExperiencesCallState);
      return updated
        ? { ...state, fetchExperiencesCallState: updated, experienceItineraries: action.experiences }
        : { ...state };
    }

    case actionTypes.UPDATE_WATCH:
      return {
        ...state,
        updateWatchCallState: CallState.InProcess,
      };

    case actionTypes.SET_UPDATE_WATCH_CALL_STATE:
      return {
        ...state,
        updateWatchCallState: action.callState,
      };

    case actionTypes.DELETE_WATCH:
      return {
        ...state,
        deleteWatchCallState: CallState.InProcess,
      };

    case actionTypes.SET_DELETE_WATCH_CALL_STATE:
      return {
        ...state,
        deleteWatchCallState: action.callState,
      };

    case actionTypes.ACCEPT_SCHEDULE_CHANGE:
      return {
        ...state,
        acceptScheduleChangeCallState: CallState.InProcess,
      };

    case actionTypes.DENY_SCHEDULE_CHANGE:
      return {
        ...state,
        denyScheduleChangeCallState: CallState.InProcess,
      };

    case actionTypes.SET_ACCEPT_SCHEDULE_CHANGE_CALL_STATE:
      return {
        ...state,
        acceptScheduleChangeCallState: action.callState,
      };

    case actionTypes.SET_DENY_SCHEDULE_CHANGE_CALL_STATE:
      return {
        ...state,
        denyScheduleChangeCallState: action.callState,
      };

    case actionTypes.LIST_WATCHES:
      return {
        ...state,
        listWatchCallState: CallState.InProcess,
      };

    case actionTypes.SET_LIST_WATCHES_CALL_STATE:
      return {
        ...state,
        listWatchCallState: action.callState,
      };

    case actionTypes.SET_WATCHES:
      return {
        ...state,
        watchContext: action.watches.tripContext,
        watches: action.watches.watches || [],
      };

    case actionTypes.CLEAR_ALL_SELECTIONS:
      return {
        ...state,
        selectedCar: null,
        selectedFlight: null,
        selectedHotel: null,
        selectedHome: null,
      };

    case actionTypes.SET_SELECTED_FLIGHT:
      return {
        ...state,
        selectedFlight: action.selectedFlight,
      };

    case actionTypes.SET_OPEN_MODAL:
      return {
        ...state,
        openModal: action.openModal,
      };

    case actionTypes.SET_SELECTED_REQUEST_PASSENGER_EDIT:
      return {
        ...state,
        requestPassengerEdit: action.requestPassengerEdit || null,
      };

    case actionTypes.SET_SELECTED_HOTEL:
      return {
        ...state,
        selectedHotel: action.selectedHotel,
      };

    case actionTypes.SET_SELECTED_HOME:
      return {
        ...state,
        selectedHome: action.selectedHome,
      };

    case actionTypes.SET_SELECTED_CAR:
      return {
        ...state,
        selectedCar: action.selectedCar,
      };

    case actionTypes.SET_SELECTED_PACKAGE:
      return {
        ...state,
        selectedPackage: action.selectedPackage,
      };
    case actionTypes.SET_SELECTED_PACKAGE_HOTEL:
      return {
        ...state,
        selectedPackageHotel: action.selectedHotel,
      };

    case actionTypes.SET_SELECTED_PACKAGE_FLIGHT:
      return {
        ...state,
        selectedPackageFlight: action.selectedFlight,
      };

    case actionTypes.SET_SELECTED_EXPERIENCE:
      return {
        ...state,
        selectedExperience: action.selectedExperience,
      };

    case actionTypes.FETCH_FLIGHT_CFAR_CANCELLATION_INFO_V3:
      return {
        ...state,
        fetchFlightCfarInfoCallState: CallState.InProcess,
        selectedCfar: null,
        selectedCfarItineraryId: action.itineraryId,
      };

    case actionTypes.SET_FLIGHT_CFAR_CANCELLATION_INFO_V3_RESPONSE:
      return {
        ...state,
        fetchFlightCfarInfoCallState: CallState.Success,
        selectedCfar: action.response,
        fetchFlightCfarInfoCallExpires: action.expires,
      };

    case actionTypes.SET_FETCH_FLIGHT_CFAR_CANCELLATION_INFO_V3_CALL_STATE_FAILED:
      return {
        ...state,
        fetchFlightCfarInfoCallState: CallState.Failed,
      };

    case actionTypes.RESET_FETCH_FLIGHT_CFAR_CANCELLATION_INFO:
      return {
        ...state,
        fetchFlightCfarInfoCallState: CallState.NotCalled,
        selectedCfar: null,
        selectedCfarItineraryId: null,
        fetchFlightCfarInfoCallExpires: null,
      };

    case actionTypes.CONFIRM_FLIGHT_CFAR_CANCELLATION:
      return {
        ...state,
        flightCfarCancellationResponse: null,
        confirmFlightCfarCancellationCallState: CallState.InProcess,
      };

    case actionTypes.SET_CONFIRM_FLIGHT_CFAR_CANCELLATION_RESPONSE:
      return {
        ...state,
        flightCfarCancellationResponse: action.response,
        confirmFlightCfarCancellationCallState: CallState.Success,
      };

    case actionTypes.SET_CONFIRM_FLIGHT_CFAR_CANCELLATION_CALL_STATE_FAILED:
      return {
        ...state,
        confirmFlightCfarCancellationCallState: CallState.Failed,
      };

    case actionTypes.SET_CONFIRM_FLIGHT_CFAR_CANCELLATION_CALL_STATE_NOT_CALLED:
      return {
        ...state,
        confirmFlightCfarCancellationCallState: CallState.NotCalled,
      };

    case actionTypes.SET_CROSS_SELL_HOTEL_AVAILABILITY_RESULTS:
      return {
        ...state,
        crossSellLatency: action.latency,
        hotelAvailabilityResponse: {
          ...action.payload,
          lodgings: [
            ...(state.hotelAvailabilityResponse?.lodgings || []),
            ...action.payload.lodgings,
          ],
          bestOverallOffer:
            action.payload.bestOverallOffer ??
            state.hotelAvailabilityResponse?.bestOverallOffer,
        },
        hotelAvailabilityCallState: HotelAvailabilityCallState.Complete,
      };

    case actionTypes.SET_SEARCHED_DATES:
      return {
        ...state,
        hotelSearchFromDate: action.searchedFromDate,
        hotelSearchUntilDate: action.searchedUntilDate,
      };
    case actionTypes.SET_SEARCHED_LOCATION_RESULT:
      return {
        ...state,
        hotelSearchLocationResult: action.searchedLocationResult,
      };
    case actionTypes.SET_SEARCHED_OCCUPANCY_COUNTS:
      const { adults, children } = action.counts as GuestsSelection;
      return {
        ...state,
        hotelSearchAdultsCount: adults,
        hotelSearchChildrenCount: children,
      };
    case actionTypes.SET_SELECTED_LODGING_INDEX:
      return {
        ...state,
        hotelSelectedLodgingIndex: action.index,
      };
    case actionTypes.SET_FLIGHT_TO_HOTEL_XSELL_TRIP_CATEGORY:
      return { ...state, flightToHotelCrossSellTripCategory: action.category };
    case actionTypes.FETCH_HOTEL_CFAR_CANCELLATION_POLICY:
      return {
        ...state,
        hotelCfarCancellationPolicyCallState: CallState.InProcess,
        hotelCfarCancellationPolicyScenario: null,
        selectedHotelCfarReservationId: action.request.reservationId,
      };
    case actionTypes.SET_HOTEL_CFAR_CANCELLATION_POLICY_SCENARIO:
      return {
        ...state,
        hotelCfarCancellationPolicyCallState: CallState.Success,
        hotelCfarCancellationPolicyScenario: action.response,
        hotelCfarCancellationPolicyCallExpires: action.expires,
      };
    case actionTypes.SET_HOTEL_CFAR_CANCELLATION_POLICY_CALL_STATE:
      return {
        ...state,
        hotelCfarCancellationPolicyCallState: action.callState,
      };
    case actionTypes.RESET_HOTEL_CFAR_CANCELLATION:
      return {
        ...state,
        hotelCfarCancellationPolicyScenario: null,
        hotelCfarCancellationPolicyCallState: CallState.NotCalled,
        hotelCfarCancellationPolicyCallExpires: null,
        selectedHotelCfarReservationId: "",
      };
    case actionTypes.CONFIRM_HOTEL_CFAR_CANCELLATION:
      return {
        ...state,
        confirmHotelCfarCancellationCallState: CallState.InProcess,
      };
    case actionTypes.SET_CONFIRM_HOTEL_CFAR_CANCELLATION_CALL_STATE:
      return {
        ...state,
        confirmHotelCfarCancellationCallState: action.callState,
      };
    case actionTypes.SET_HOTEL_CFAR_CANCELLED:
      return {
        ...state,
        hotelCfarCancellationPolicyScenario: {
          HotelCfarCancelScenario: HotelCfarCancelScenarioEnum.Canceled,
        },
        confirmHotelCfarCancellationCallState: CallState.Success,
      };
    case actionTypes.LIST_PAYMENT_METHODS:
      return {
        ...state,
        listPaymentMethodCallState: CallState.InProcess,
      };

    case actionTypes.SET_PAYMENT_METHODS:
      return {
        ...state,
        paymentMethods: action.paymentMethods,
        listPaymentMethodCallState: CallState.Success,
      };

    case actionTypes.SET_PAYMENT_METHODS_CALL_STATE_FAILED:
      return {
        ...state,
        listPaymentMethodCallState: CallState.Failed,
      };

    case actionTypes.SET_TRIP_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.searchQuery,
      };
    case actionTypes.SET_CROSS_SELL_OFFERS:
      return { ...state, crossSellOffers: action.offers };
    case actionTypes.SET_FETCH_ACTIVE_HOTEL_CROSS_SELL_CALLSTATE:
      return { ...state, fetchHotelCrossSellCallState: action.callState };
    case actionTypes.FETCH_ACTIVITIES_XSELL:
      return { ...state, activitiesXsellCallState: CallState.InProcess };
    case actionTypes.SET_ACTIVITIES_XSELL_RESULTS:
      return {
        ...state,
        activitiesXsellExperiences: action.experiences,
        activitiesXsellCallState: CallState.Success,
      };
    case actionTypes.SET_ACTIVITIES_XSELL_CALL_STATE:
      return { ...state, activitiesXsellCallState: action.callState };
    default:
      return state;
  }
}

export * from "./selectors";
